import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link, useBlocker } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { AlertCircle, Save, X, Archive } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import PracticeFinance from "../components/PracticeFinance";

// Ritorna un valore adatto agli <input>: null/undefined → "".
const s = (v) => (v == null ? "" : String(v));

export default function PracticeEditPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { has } = useAuth();
  const [p, setP] = useState(null);
  const [lookups, setLookups] = useState(null);
  const [users, setUsers] = useState([]);
  const [f, setF] = useState(null);
  const [initial, setInitial] = useState(null);
  const [err, setErr] = useState(null);
  const [fieldErr, setFieldErr] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const [pr, lk, us] = await Promise.all([
        api.get(`/practices/${id}`),
        api.get("/lookups/all"),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setP(pr.data);
      setLookups(lk.data);
      setUsers(us.data || []);
      const init = {
        title: s(pr.data.title),
        service_type: s(pr.data.service_type),
        tool_note: s(pr.data.tool_note),
        responsible_user_id: s(pr.data.responsible_user_id),
        opening_date: s(pr.data.opening_date),
        deadline: s(pr.data.deadline),
        priority: s(pr.data.priority) || "media",
        phase_id: s(pr.data.phase?.id),
        status_id: s(pr.data.status?.id),
        outcome_id: s(pr.data.outcome?.id),
        project_amount: s(pr.data.project_amount),
        eligible_amount: s(pr.data.eligible_amount),
        requested_grant: s(pr.data.requested_grant),
        granted_grant: s(pr.data.granted_grant),
        requested_loan: s(pr.data.requested_loan),
        approved_loan: s(pr.data.approved_loan),
        disbursed_loan: s(pr.data.disbursed_loan),
        own_funds: s(pr.data.own_funds),
        vat_amount: s(pr.data.vat_amount),
        application_number: s(pr.data.application_number),
        manager_body: s(pr.data.manager_body),
        cup: s(pr.data.cup),
        cor: s(pr.data.cor),
        description: s(pr.data.description),
        issues: s(pr.data.issues),
        next_steps: s(pr.data.next_steps),
      };
      setF(init);
      setInitial(init);
    })();
  }, [id]);

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  const isDirty = useMemo(() => {
    if (!f || !initial) return false;
    return Object.keys(f).some((k) => (f[k] ?? "") !== (initial[k] ?? ""));
  }, [f, initial]);

  // Avviso uscita pagina con modifiche non salvate (chiusura browser / F5 / link esterno)
  useEffect(() => {
    const h = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [isDirty]);

  // Blocker interno (createBrowserRouter data router) — protegge navigazione interna via sidebar
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    isDirty && currentLocation.pathname !== nextLocation.pathname
  );
  useEffect(() => {
    if (blocker && blocker.state === "blocked") {
      if (window.confirm("Hai modifiche non salvate. Vuoi uscire senza salvare?")) blocker.proceed();
      else blocker.reset();
    }
  }, [blocker?.state]); // eslint-disable-line

  const selectedStatus = useMemo(
    () => lookups?.statuses?.find((x) => x.id === f?.status_id) || null,
    [lookups, f]
  );
  const isClosing = !!selectedStatus?.is_closing;

  // Auto-clear outcome quando lo stato non è finale
  useEffect(() => {
    if (!f) return;
    if (!isClosing && f.outcome_id) {
      set("outcome_id", "");
    }
  }, [isClosing]); // eslint-disable-line

  const validate = () => {
    const e = {};
    if (!f.title.trim()) e.title = "Il titolo è obbligatorio";
    if (!f.service_type) e.service_type = "Il tipo servizio è obbligatorio";
    if (!f.opening_date) e.opening_date = "La data di apertura è obbligatoria";
    if (f.deadline && f.opening_date && f.deadline < f.opening_date)
      e.deadline = "La scadenza non può essere precedente all'apertura";
    if (isClosing && !f.outcome_id)
      e.outcome_id = "Con stato finale, l'esito è obbligatorio";
    if (!isClosing && f.outcome_id)
      e.outcome_id = "L'esito è ammesso solo con stato finale";
    const numFields = [
      "project_amount", "eligible_amount", "requested_grant", "granted_grant",
      "requested_loan", "approved_loan", "disbursed_loan", "own_funds", "vat_amount",
    ];
    for (const k of numFields) {
      if (f[k] !== "" && f[k] != null && isNaN(parseFloat(f[k])))
        e[k] = "Valore numerico non valido";
    }
    return e;
  };

  const buildPayload = (form) => {
    const num = (v) => (v === "" || v == null ? null : parseFloat(v));
    const str = (v) => (v === "" || v == null ? null : v);
    const uid = (v) => (v === "" || v == null ? null : v);
    const dt = (v) => (v === "" || v == null ? null : v);
    return {
      title: form.title.trim(),
      service_type: form.service_type,
      tool_note: str(form.tool_note),
      responsible_user_id: uid(form.responsible_user_id),
      opening_date: dt(form.opening_date),
      deadline: dt(form.deadline),
      priority: form.priority,
      phase_id: uid(form.phase_id),
      status_id: uid(form.status_id),
      outcome_id: uid(form.outcome_id),
      project_amount: num(form.project_amount),
      eligible_amount: num(form.eligible_amount),
      requested_grant: num(form.requested_grant),
      granted_grant: num(form.granted_grant),
      requested_loan: num(form.requested_loan),
      approved_loan: num(form.approved_loan),
      disbursed_loan: num(form.disbursed_loan),
      own_funds: num(form.own_funds),
      vat_amount: num(form.vat_amount),
      application_number: str(form.application_number),
      manager_body: str(form.manager_body),
      cup: str(form.cup),
      cor: str(form.cor),
      description: str(form.description),
      issues: str(form.issues),
      next_steps: str(form.next_steps),
    };
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    const errs = validate();
    setFieldErr(errs);
    if (Object.keys(errs).length) {
      setErr("Correggere gli errori evidenziati.");
      return;
    }
    // Rilevamento riapertura: da stato finale → non finale
    const initialStatus = lookups?.statuses?.find((x) => x.id === initial.status_id);
    if (initialStatus?.is_closing && !isClosing) {
      const ok = window.confirm(
        "Stai riaprendo una pratica chiusa/rinunciata. L'esito verrà azzerato e la riapertura sarà registrata nello storico. Confermi?"
      );
      if (!ok) return;
    }

    setBusy(true);
    try {
      const payload = buildPayload(f);
      await api.patch(`/practices/${id}`, payload);
      toast.success("Pratica aggiornata con successo.");
      setInitial(f); // reset "dirty"
      // Torna al dettaglio
      nav(`/practices/${id}`);
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      setErr(typeof d === "string" ? d : d?.message || "Errore nel salvataggio");
    } finally {
      setBusy(false);
    }
  };

  const archive = async () => {
    if (!window.confirm(`Archiviare la pratica ${p.code}? Potrà essere ripristinata da un amministratore.`))
      return;
    try {
      await api.delete(`/practices/${id}`);
      toast.success("Pratica archiviata.");
      nav("/practices");
    } catch { /* toast già gestito */ }
  };

  if (!p || !lookups || !f) return <div className="ma-loading">Caricamento…</div>;

  const Err = ({ k }) => fieldErr[k] ? <div className="ma-field-err"><AlertCircle size={12}/> {fieldErr[k]}</div> : null;

  return (
    <div className="ma-page">
      <div className="ma-page-header">
        <div>
          <div className="ma-crumb"><Link to={`/practices/${id}`}>← Dettaglio pratica</Link></div>
          <h1 className="ma-h1" data-testid="edit-practice-title">
            <span className="ma-mono ma-code-badge">{p.code}</span> Modifica pratica
          </h1>
          <div className="ma-sub">
            <Link to={`/clients/${p.subject.id}`} className="ma-link">{p.subject.display_name}</Link>
            {isDirty && <><span>·</span><span className="ma-pill pill-warn">Modifiche non salvate</span></>}
          </div>
        </div>
        <div className="ma-actions">
          {has("practices.write") && (
            <button type="button" className="ma-btn-danger-sm" onClick={archive} data-testid="btn-archive-practice">
              <Archive size={14}/> Archivia
            </button>
          )}
        </div>
      </div>

      <form onSubmit={submit} className="ma-form-panel" noValidate>
        <h3 className="ma-h3">Dati generali</h3>
        <div className="ma-form-grid">
          <div className="ma-col-2"><label className="ma-label">Titolo *</label>
            <input className={`ma-input ${fieldErr.title?"err":""}`} value={f.title} onChange={e=>set("title",e.target.value)} data-testid="edit-title"/>
            <Err k="title"/></div>
          <div><label className="ma-label">Tipo servizio *</label>
            <select className={`ma-input ${fieldErr.service_type?"err":""}`} value={f.service_type} onChange={e=>set("service_type",e.target.value)} data-testid="edit-service">
              {lookups.service_types.map(x => <option key={x.code} value={x.code}>{x.label}</option>)}
            </select>
            <Err k="service_type"/></div>
          <div><label className="ma-label">Strumento / misura</label>
            <input className="ma-input" value={f.tool_note} onChange={e=>set("tool_note",e.target.value)}/></div>
          <div><label className="ma-label">Data apertura *</label>
            <input type="date" className={`ma-input ${fieldErr.opening_date?"err":""}`} value={f.opening_date} onChange={e=>set("opening_date",e.target.value)}/>
            <Err k="opening_date"/></div>
          <div><label className="ma-label">Scadenza</label>
            <input type="date" className={`ma-input ${fieldErr.deadline?"err":""}`} value={f.deadline} onChange={e=>set("deadline",e.target.value)}/>
            <Err k="deadline"/></div>
          <div><label className="ma-label">Priorità</label>
            <select className="ma-input" value={f.priority} onChange={e=>set("priority",e.target.value)}>
              <option value="bassa">Bassa</option><option value="media">Media</option>
              <option value="alta">Alta</option><option value="urgente">Urgente</option>
            </select></div>
          <div><label className="ma-label">Responsabile</label>
            <select className="ma-input" value={f.responsible_user_id} onChange={e=>set("responsible_user_id",e.target.value)} data-testid="edit-responsible">
              <option value="">—</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
            </select></div>
        </div>

        <h3 className="ma-h3">Fase, stato ed esito</h3>
        <div className="ma-form-grid">
          <div><label className="ma-label">Fase</label>
            <select className="ma-input" value={f.phase_id} onChange={e=>set("phase_id",e.target.value)} data-testid="edit-phase">
              <option value="">—</option>
              {lookups.phases.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select></div>
          <div><label className="ma-label">Stato</label>
            <select className="ma-input" value={f.status_id} onChange={e=>set("status_id",e.target.value)} data-testid="edit-status">
              <option value="">—</option>
              {lookups.statuses.map(x => (
                <option key={x.id} value={x.id}>
                  {x.name}{x.is_closing ? " (finale)" : ""}
                </option>
              ))}
            </select>
            <div className="ma-hint">
              {isClosing
                ? "Stato finale: l'esito è obbligatorio."
                : "Stato non finale: l'esito deve restare vuoto."}
            </div>
          </div>
          <div><label className="ma-label">Esito {isClosing && <span className="ma-req">*</span>}</label>
            <select
              className={`ma-input ${fieldErr.outcome_id?"err":""}`}
              value={f.outcome_id}
              onChange={e=>set("outcome_id",e.target.value)}
              disabled={!isClosing}
              data-testid="edit-outcome"
            >
              <option value="">—</option>
              {lookups.outcomes.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
            <Err k="outcome_id"/></div>
        </div>

        <h3 className="ma-h3">Riferimenti amministrativi</h3>
        <div className="ma-form-grid">
          <div><label className="ma-label">Numero domanda</label>
            <input className="ma-input" value={f.application_number} onChange={e=>set("application_number",e.target.value)}/></div>
          <div><label className="ma-label">Ente gestore</label>
            <input className="ma-input" value={f.manager_body} onChange={e=>set("manager_body",e.target.value)}/></div>
          <div><label className="ma-label">CUP</label>
            <input className="ma-input ma-mono" value={f.cup} onChange={e=>set("cup",e.target.value)}/></div>
          <div><label className="ma-label">COR</label>
            <input className="ma-input ma-mono" value={f.cor} onChange={e=>set("cor",e.target.value)}/></div>
        </div>

        <h3 className="ma-h3">Dati economici</h3>
        <div className="ma-form-grid">
          <div><label className="ma-label">Importo progetto (€)</label>
            <input type="number" step="0.01" className={`ma-input ${fieldErr.project_amount?"err":""}`} value={f.project_amount} onChange={e=>set("project_amount",e.target.value)}/>
            <Err k="project_amount"/></div>
          <div><label className="ma-label">Importo ammissibile (€)</label>
            <input type="number" step="0.01" className="ma-input" value={f.eligible_amount} onChange={e=>set("eligible_amount",e.target.value)}/></div>
          <div><label className="ma-label">IVA (€)</label>
            <input type="number" step="0.01" className="ma-input" value={f.vat_amount} onChange={e=>set("vat_amount",e.target.value)}/></div>
          <div><label className="ma-label">Mezzi propri (€)</label>
            <input type="number" step="0.01" className="ma-input" value={f.own_funds} onChange={e=>set("own_funds",e.target.value)}/></div>
          <div><label className="ma-label">Contributo richiesto (€)</label>
            <input type="number" step="0.01" className="ma-input" value={f.requested_grant} onChange={e=>set("requested_grant",e.target.value)}/></div>
          <div><label className="ma-label">Contributo concesso (€)</label>
            <input type="number" step="0.01" className="ma-input" value={f.granted_grant} onChange={e=>set("granted_grant",e.target.value)}/></div>
          <div><label className="ma-label">Finanziamento richiesto (€)</label>
            <input type="number" step="0.01" className="ma-input" value={f.requested_loan} onChange={e=>set("requested_loan",e.target.value)}/></div>
          <div><label className="ma-label">Finanziamento approvato (€)</label>
            <input type="number" step="0.01" className="ma-input" value={f.approved_loan} onChange={e=>set("approved_loan",e.target.value)}/></div>
          <div><label className="ma-label">Erogato (€)</label>
            <input type="number" step="0.01" className="ma-input" value={f.disbursed_loan} onChange={e=>set("disbursed_loan",e.target.value)}/></div>
        </div>

        <h3 className="ma-h3">Descrizione e note operative</h3>
        <div className="ma-form-grid">
          <div className="ma-col-full"><label className="ma-label">Descrizione</label>
            <textarea className="ma-input" rows={3} value={f.description} onChange={e=>set("description",e.target.value)}/></div>
          <div className="ma-col-full"><label className="ma-label">Criticità / problemi</label>
            <textarea className="ma-input" rows={2} value={f.issues} onChange={e=>set("issues",e.target.value)}/></div>
          <div className="ma-col-full"><label className="ma-label">Prossimi passi</label>
            <textarea className="ma-input" rows={2} value={f.next_steps} onChange={e=>set("next_steps",e.target.value)}/></div>
        </div>

        {err && <div className="ma-error" data-testid="edit-error">{err}</div>}
        <div className="ma-form-actions">
          <button type="button" className="ma-btn-secondary" onClick={() => {
            if (isDirty && !window.confirm("Annullare le modifiche non salvate?")) return;
            nav(`/practices/${id}`);
          }} data-testid="btn-cancel-edit"><X size={14}/> Annulla</button>
          <button type="submit" className="ma-btn-primary" disabled={busy || !isDirty} data-testid="btn-save-practice">
            <Save size={14}/> {busy ? "Salvataggio…" : "Salva modifiche"}
          </button>
        </div>
      </form>
      <PracticeFinance practiceId={id} />
    </div>
  );
}
